import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    ElementRef,
    inject,
    input,
    OnChanges,
    OnInit,
    output,
    signal
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';

import { startWith } from 'rxjs/operators';

import { ShFormDateSelectComponent } from '../../../../../form-controls/components/form-date-select/form-date-select.component';
import { DateFields, DateRange } from '../../../../../core/dates/entities/date-range.entity';
import { ReduceToEnteredDateFields } from '../../../../../core/dates/helpers/date-range-granularity.helper';
import {
    CreateDateRangeQuery,
    IsLaxDateRangeQuery,
    ParseDateRangeQuery
} from '../../../../../core/dates/helpers/date-range-query-helper';
import { DatePart, isCompleteDatePart, isFromBeforeTo } from '../../../../../core/dates/helpers/date-range-validation.helper';

/**
 * Compact from/to date-range control for a per-column filter popover. Reuses the vendor
 * `sh-form-date-select` Year/Month/Day inputs and the same range validation the advanced-search
 * date filter applies, and speaks the shared Lucene `[start TO end]` range-query string.
 */
@Component({
    standalone: true,
    selector: 'sh-column-date-filter',
    templateUrl: './column-date-filter.component.html',
    styleUrls: ['./column-date-filter.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ReactiveFormsModule, TranslateModule, ShFormDateSelectComponent]
})
export class ColumnDateFilterComponent implements OnChanges, OnInit {
    private readonly formBuilder: UntypedFormBuilder = inject(UntypedFormBuilder);
    private readonly destroyRef: DestroyRef = inject(DestroyRef);
    private readonly hostElement: ElementRef<HTMLElement> = inject(ElementRef);

    readonly fromId = 'column-date-filter-from';
    readonly toId = 'column-date-filter-to';

    /** Current column-filter value, a `[start TO end]` range query string (or empty). */
    value = input<string>('');

    /** Emits the new range query string, or an empty string to clear the filter. */
    rangeChanged = output<string>();

    readonly form: UntypedFormGroup = this.formBuilder.group({
        From: this.buildDateGroup(),
        To: this.buildDateGroup()
    });

    /** Translation key of the current validation error, or `null` when the range is valid. */
    readonly error = signal<string | null>(null);
    readonly canApply = computed<boolean>(() => this.error() === null);

    get fromGroup(): UntypedFormGroup {
        return this.form.get('From') as UntypedFormGroup;
    }

    get toGroup(): UntypedFormGroup {
        return this.form.get('To') as UntypedFormGroup;
    }

    /** The value currently reflected in the form, used to ignore the echo of our own apply/clear when re-seeding from `value`. */
    private reflectedValue = '';

    ngOnChanges(): void {
        const value: string = this.value();
        // Re-seed whenever `value` changes externally — e.g. clear-all resets it to '' — but skip the
        // round-trip of our own apply/clear so it never fights the form the user is editing.
        if (value === this.reflectedValue) {
            return;
        }
        this.reflectedValue = value;
        this.seedFromValue(value);
    }

    ngOnInit(): void {
        this.form.valueChanges
            .pipe(startWith(this.form.value), takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.validate());
    }

    apply(): void {
        if (!this.canApply()) {
            return;
        }

        const range: DateRange = this.buildRange();
        const hasBound: boolean = range.From.Year !== null || range.Until.Year !== null;

        this.emit(hasBound ? CreateDateRangeQuery(range) : '');
    }

    /**
     * Focuses the range's first field — the From year — so an opening popover is immediately typeable.
     * Scoped to this host because every instance renders the same field ids.
     */
    focusFromField(): void {
        this.hostElement.nativeElement.querySelector<HTMLInputElement>(`#${this.fromId}-year`)?.focus();
    }

    clear(): void {
        this.form.reset();
        // Resetting can surface a transient "incomplete"/"invalid" state as the Year/Month/Day
        // controls clear one by one; a cleared filter is never invalid, so drop any error.
        this.error.set(null);
        this.emit('');
    }

    private emit(range: string): void {
        this.reflectedValue = range;
        this.rangeChanged.emit(range);
    }

    private seedFromValue(value: string): void {
        this.form.reset();

        if (!IsLaxDateRangeQuery(value)) {
            this.error.set(null);
            return;
        }

        const range: DateRange = ParseDateRangeQuery(value);
        // Collapse the stored inclusive-range expansion back to the granularity the user entered, so a
        // year/month-only bound reopens with empty month/day inputs instead of the phantom expanded day.
        this.fromGroup.patchValue(ReduceToEnteredDateFields(range.From, true));
        this.toGroup.patchValue(ReduceToEnteredDateFields(range.Until, false));
    }

    private validate(): void {
        const from: DatePart = this.readPart(this.fromGroup);
        const to: DatePart = this.readPart(this.toGroup);

        if (!isCompleteDatePart(from) || !isCompleteDatePart(to)) {
            this.error.set('column-filter.incomplete-date');
            return;
        }

        if (from.Year !== null && to.Year !== null && !isFromBeforeTo(from, to)) {
            this.error.set('column-filter.invalid-range');
            return;
        }

        this.error.set(null);
    }

    private buildRange(): DateRange {
        return {
            From: this.toDateFields(this.readPart(this.fromGroup)),
            Until: this.toDateFields(this.readPart(this.toGroup)),
            LengthInDays: Number.NaN
        };
    }

    private readPart(group: UntypedFormGroup): DatePart {
        return {
            Year: this.toNumberOrNull(group.get('Year')!.value),
            Month: this.toNumberOrNull(group.get('Month')!.value),
            Day: this.toNumberOrNull(group.get('Day')!.value)
        };
    }

    private toNumberOrNull(value: unknown): number | null {
        return value === null || value === undefined || value === '' ? null : Number(value);
    }

    private toDateFields(part: DatePart): DateFields {
        return { Year: part.Year, Month: part.Month, Day: part.Day, Date: null };
    }

    private buildDateGroup(): UntypedFormGroup {
        return this.formBuilder.group({ Year: [null], Month: [null], Day: [null] });
    }
}
